import { useCallback, useEffect, useRef, useState } from "react";

/** Pane width persisted to localStorage; returns the width and a mouse-down handler for the resizer. */
export function useResizable(key: string, initial: number, min: number, max: number, direction: 1 | -1 = 1) {
  const [width, setWidth] = useState<number>(() => {
    try {
      const v = Number(localStorage.getItem(key));
      return v >= min && v <= max ? v : initial;
    } catch {
      return initial;
    }
  });
  const [active, setActive] = useState(false);
  const start = useRef<{ x: number; w: number } | null>(null);

  const onMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      start.current = { x: e.clientX, w: width };
      setActive(true);
    },
    [width],
  );

  useEffect(() => {
    if (!active) return;
    const onMove = (e: MouseEvent) => {
      if (!start.current) return;
      const next = Math.min(max, Math.max(min, start.current.w + (e.clientX - start.current.x) * direction));
      setWidth(next);
    };
    const onUp = () => {
      setActive(false);
      start.current = null;
      setWidth((w) => {
        try {
          localStorage.setItem(key, String(w));
        } catch {
          // ignore
        }
        return w;
      });
    };
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [active, key, min, max, direction]);

  return { width, onMouseDown, active };
}
